import { Link, useLoaderData, useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
// i18n
import { useTranslation } from 'react-i18next'

// context
import { NewContractContext } from './context'

// components
import { PageLayout } from '@/layouts/page-layout'
import { GroupInput } from '@/components/v2/group-input'
import { Button, Input, Switch, Typography } from '@material-tailwind/react'
import { Form, Formik } from 'formik'

// forms
import { GeneralInformationForm } from './general-information-form'
import { AccountContactForm } from './account-contact-form.index'
import { ContractAmountsForm } from './contract-amounts-form'
import { AssetAmountForm } from './asset-amounts-form'
// utils
import { contractServices } from '@/services'
import PropTypes from 'prop-types'
import { StatusSwitcher } from '@/components/status-switcher'
import { ConfirmationModal } from '@/components/confirmation-modal'
import { useAuthorization } from '@/hooks'
import { HasAccess } from '@permify/react-role'

export const ContractCreate = ({ editMode }) => {
  const { hasAccess } = useAuthorization({
    redirection: {
      enable: true
    }
  })
  const actionType = editMode ? 'update' : 'create'

  const navigate = useNavigate()
  const defaultInitialValues = {
    name: '',
    contractor_company_id: null,
    account_office_ids: null,
    account_managment_id: null,
    start_date: null,
    finish_date: null,
    user_id: null,
    ancestry: null,
    contact: {
      name: '',
      email: '',
      document_number: ''
    },
    total_amount_cents: 0,
    assets_amount_cents: 0,
    attachments_attributes: []
  }

  // data
  // currentContractData its only for editMode
  const [
    usersData,
    currentContractData
  ] = useLoaderData()

  // states
  const [initialValues, setInitialValues] = useState(defaultInitialValues)
  const [currentContract, setCurrentContract] = useState(
    currentContractData?.data?.contract || {}
  )
  const [openStatusSwitcherModal, setOpenStatusSwitcherModal] = useState(false)
  const [openAccreditedSwitcherModal, setOpenAccreditedSwitcherModal] =
    useState(false)

  const {
    data: { users }
  } = usersData

  const onSubmit = async (values) => {
    contractServices[actionType]({
      id: editMode && currentContract.id,
      body: { contract: values }
    }).then((response) => {
      if (response.ok) {
        navigate('/contracts')
      }
    })
  }

  const [withAssetAmounts, setWithAssetAmounts] = useState(
    currentContractData?.data?.contract?.assetsAmountCents > 0
  )

  useEffect(() => {
    if (editMode) {
      hasAccess('contract#edit')
    } else {
      hasAccess('contract#create')
    }
  }, [])

  // effects
  useEffect(() => {
    if (!editMode) return

    const fetchInitialValues = async () => {
      setInitialValues({
        name: currentContract.name,
        contractor_company_id: currentContract.company.id,
        office_ids: currentContract.offices.map(office => office.id),
        account_managment_id: currentContract.subManagmentId,
        start_date: currentContract.startDate,
        finish_date: currentContract.finishDate,
        ancestry: currentContract.ancestry,
        user_id: currentContract?.user?.id,
        contact: {
          name: currentContract.contact.name,
          email: currentContract.contact.email,
          document_number: currentContract.contact.documentNumber
        },
        total_amount_cents: currentContract.totalAmountCents,
        assets_amount_cents: currentContract.assetsAmountCents
      })
    }

    fetchInitialValues()
  }, [currentContract])

  const handleSwitchStatus = async (_event) => {
    try {
      await contractServices.update({
        id: currentContract.id,
        body: {
          contract: {
            status: currentContract.status === 'active' ? 'inactive' : 'active'
          }
        }
      })
      getCurrentContract()
    } catch ({ response }) { }
  }

  const handleSwitchAccredited = async (_event) => {
    try {
      const newAccreditedState =
        currentContract.accredited === 'active' ? 'inactive' : 'active'
      await contractServices.update({
        id: currentContract.id,
        body: {
          contract: {
            accredited: newAccreditedState
          }
        }
      })
      getCurrentContract()
    } catch ({ response }) { }
  }

  const getCurrentContract = useCallback(async () => {
    try {
      const {
        data: { contract }
      } = await contractServices.show({
        id: currentContract.id
      })

      setCurrentContract(contract)
    } catch ({ response }) { }
  }, [])

  const { t } = useTranslation()

  return (
    <PageLayout
      editMode={editMode}
      title={t(`actions.${actionType}.with`, {
        what: t('contract.one').toLowerCase()
      })}
      rightSection={
        editMode && (
          <>
            <HasAccess permissions='contract#activate'>
              <StatusSwitcher
                label={'contract'}
                type={'status'}
                value={currentContract?.status}
                onChange={() => setOpenStatusSwitcherModal(true)}
              />
            </HasAccess>
            <HasAccess permissions='contract#accredit'>
              <StatusSwitcher
                label={'contract'}
                type={'accredited'}
                value={currentContract?.accredited}
                onChange={() => setOpenAccreditedSwitcherModal(true)}
              />
            </HasAccess>
          </>
        )
      }
    >
      <ConfirmationModal
        onConfirm={handleSwitchStatus}
        open={openStatusSwitcherModal}
        handler={() => setOpenStatusSwitcherModal(!open)}
        title={t(`modal.confirm.status.${currentContract.status}.title`)}
        message={t(`modal.confirm.status.${currentContract.status}.message`)}
      />

      <ConfirmationModal
        onConfirm={handleSwitchAccredited}
        open={openAccreditedSwitcherModal}
        handler={() => setOpenAccreditedSwitcherModal(!open)}
        title={t(
          `modal.confirm.accredited.${currentContract.accredited}.title`
        )}
        message={t(
          `modal.confirm.accredited.${currentContract.accredited}.message`
        )}
      />

      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={onSubmit}
      >
        {({ values, handleChange }) => (
          <NewContractContext.Provider
            value={{ users, currentContract }}
          >
            <Form>
              <GroupInput
                title='General'
                description='Rellena la información general del nuevo contrato.'
                className='py-14'
              >
                <GeneralInformationForm />
              </GroupInput>

              <GroupInput
                title='Contacto Mandante'
                description='Rellena la información de la persona a cargo.'
                className='py-14'
              >
                <div className='grid grid-cols-2 gap-5'>
                  <AccountContactForm />
                </div>
              </GroupInput>

              <GroupInput
                title='Contacto Contratista'
                description='Rellena la información de la persona a cargo.'
                className='py-14'
              >
                <div className='grid grid-cols-2 gap-5'>
                  <Input
                    label={t('common.fullName')}
                    name='contact.name'
                    value={values.contact.name}
                    onChange={handleChange}
                  />
                  <Input
                    label={t('documentNumber.one')}
                    value={values.contact.document_number}
                    name='contact.document_number'
                    onChange={handleChange}
                  />
                  <Input
                    label={t('email.one')}
                    value={values.contact.email}
                    name='contact.email'
                    onChange={handleChange}
                  />
                </div>
              </GroupInput>

              <GroupInput
                title='Contabilidad'
                description='Rellena la información de la persona a cargo.'
                className='py-14'
                divider={false}
              >
                <div className='grid grid-cols-3 gap-5'>
                  <ContractAmountsForm />
                </div>

                <div className='bg-gray-100 px-10 py-10 rounded-2xl grid grid-rows-2 gap-4 items-center'>
                  <div className='flex items-center justify-between gap-3'>
                    <div className='flex-[6]'>
                      <Typography
                        variant='h6'
                        className='font-semibold text-lg'
                      >
                        ¿Suministra bienes?
                      </Typography>
                      <Typography variant='small' className='text-gray-500'>
                        En caso de que el contrato esté asociado a bienes,
                        active el botón para poder rellenar el campo de monto
                        neto. Los otros campos se rellenan automáticamente.
                      </Typography>
                    </div>

                    <div className='flex-[2] flex justify-end'>
                      <Switch
                        id='withAssetAmounts'
                        checked={withAssetAmounts}
                        containerProps={{
                          className: 'w-14 h-7'
                        }}
                        className='h-full w-full'
                        onChange={({ target: { checked } }) =>
                          setWithAssetAmounts(checked)
                        }
                        ripple={false}
                        circleProps={{
                          className: 'left-3.5 border-none'
                        }}
                      />
                    </div>
                  </div>

                  <div className='grid grid-cols-3 gap-5'>
                    <AssetAmountForm enable={withAssetAmounts} />
                  </div>
                </div>
              </GroupInput>

              <div className='flex justify-end gap-3 mt-10'>
                <Link to='/contracts'>
                  <Button variant='text' type='submit'>
                    {t('actions.cancel')}
                  </Button>
                </Link>
                {
                  editMode
                    ? <HasAccess permissions='contract#update'>
                      <Button type='submit'>
                        {t('actions.update.one')}
                      </Button>
                    </HasAccess>
                    : <HasAccess permissions='contract#create'>
                      <Button type='submit'>
                        {t('actions.create.one')}
                      </Button>
                    </HasAccess>
                }
              </div>
            </Form>
          </NewContractContext.Provider>
        )}
      </Formik>
    </PageLayout>
  )
}

ContractCreate.propTypes = {
  editMode: PropTypes.bool.isRequired
}

ContractCreate.defaultProps = {
  editMode: false
}
